import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../styles/colors';
import { DefaultInput } from '../general/forms/DefaultInput';
import { SelectBox } from '../general/forms/SelectBox';
import {
  Button,
  CustomerTypes,
  GenericList, Icon, isObjectOfTypeMetaProduct,
  Product,
  ProductCategory, ProductItemType,
  useShowAlertPopup, useShowConfirmationPopup,
} from '../..';
import { LinkButton } from '../general/LinkButton';
import { SuggestionDropDown } from '../general/forms/SuggestionDropDown';
import axios from 'axios';
import { TextBox } from '../general/forms/TextBox';
import { Toggle } from '../general/forms/Toggle';
import { FileUpload, FileUploadFile } from '../general/forms/FileUpload';
import { QuestionnaireWithResultSets } from '../../interfaces/Questionnaire';
import { AdditionalProductLeadQuestionnaireListContent } from '../lists/AdditionalProductLeadQuestionnaireListContent';
import { AdditionalProductResultSetsListContent } from '../lists/AdditionalProductResultSetsListContent';
import { CopyIdButton } from '../general/CopyIdButton';
import { PRODUCT_ITEM_TYPES } from '../general/products/icons/ProductIconWrapper';
import { DropDown } from '../general/forms/DropDown';

export interface ProductFormProps {
  product?: Product | null;
  setProduct: (payload: { product: Product, attachments: FileUploadFile[] }) => void;
  onCancel?: () => void;
}

export interface ProductFromValidationState {
  name: boolean;
  type: boolean;
  description: boolean;
  targetGroups: boolean;
  productCategoryId: boolean;
  metaProduct: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.white};
  padding: 2rem 1rem;
`;

const ToggleInnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  grid-column-start: 1;
  grid-column-end: 4;
  padding: 1.5rem 0 .5rem;

  a {
    text-overflow: ellipsis;
    width:100%;
    color: ${Colors.primary};
  }
`;

const Headline = styled.h2<{ divideContent?: boolean }>`
  display: flex;
  flex-direction: row;
  min-height: 1.5rem;
  margin-bottom: 1rem;
  margin-top: ${props => props.divideContent ? '3rem' : '0'}
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.white};
`;

const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  border: none;
  margin-bottom: 3rem;
  
  ${Headline} {
    margin-bottom: -.5rem;
    color: ${Colors.grayDark};
    font-weight: 700;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top:1rem;
  justify-content: space-between;
`;

const NoDocumentsTeaser = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
`;

const DeleteAttachmentIconWrapper = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

export const DownloadLink = styled.a`
  &:hover {
    cursor: pointer;
  }
`;

export interface QuestionnairePermission {
  id: string;
  checked: boolean;
}

export interface QuestionnairePermissionMatrix {
  id: string;
  title: string;
  type: string;
  resultSets: QuestionnairePermission[];
}

export interface LeadQuestionnaireQuestionnaireStateProp {
  id: string;
  label: string;
  checked: boolean;
}


export interface LeadQuestionnaireStateProp {
  label: string;
  questionnaires: LeadQuestionnaireQuestionnaireStateProp[]
}

export const ProductForm: FC<ProductFormProps> = (props) => {
  const { product = null, setProduct, onCancel } = props;
  const showConfirmationPopup = useShowConfirmationPopup();
  const [tempProduct, setTempProduct] = useState<Product | null>(product);
  const [tempAttachments, setTempAttachments] = useState<FileUploadFile[] | null>(null);
  const [productAttachments, setProductAttachments] = useState<FileUploadFile[] | null>(null);
  const showNotificationBanner = useShowAlertPopup();
  const initialValidationState = {
    name: false,
    type: false,
    description: false,
    targetGroups: false,
    productCategoryId: false,
    metaProduct: false,
  };
  const [validationState, setValidationState] = useState<ProductFromValidationState>(initialValidationState);
  const [categoriesList, setCategoriesList] = useState<[{ id: string, name: string }] | null>(null);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireWithResultSets[] | null>(null);
  const [leadQuestionnaires, setLeadQuestionnaires] = useState<LeadQuestionnaireStateProp[] | null>(null);
  const [filteredLeadQuestionnaires, setFilteredLeadQuestionnaires] = useState<LeadQuestionnaireStateProp[] | null>(null);
  const [filteredQuestionnaires, setFilteredQuestionnaires] = useState<QuestionnairePermissionMatrix[] | null>(null);

  const validate = () => {
    const state = {
      name: (tempProduct?.name && tempProduct?.name.length > 0) ? false : true,
      type: (tempProduct?.type && tempProduct?.type.length > 0) ? false : true,
      productCategoryId: (tempProduct?.productCategoryId && tempProduct?.productCategoryId.length > 0) ? false : true,
      description: (tempProduct?.description && tempProduct?.description.length > 0) ? false : true,
      targetGroups: (tempProduct?.targetGroups && tempProduct?.targetGroups.length > 0) ? false : true,
      eco: (tempProduct?.eco || !tempProduct?.eco) ? false : true,
      metaProduct: (tempProduct?.metaProduct && tempProduct?.metaProduct !== null) ? false : true,
    };
    setValidationState(state);
    return state;
  };

  const refreshFilteredQuestionnaires = () => {
    const checkIfIsChecked = (type: string, id: string) => {
      if (type === 'BUSINESS') {
        return product.resultSets?.business?.includes(id) || false;
      } else if (type === 'MAJOR_CUSTOMERS') {
        return product.resultSets?.majorCustomers?.includes(id) || false;
      } else if (type === 'HOUSING_INDUSTRY') {
        return product.resultSets?.housingIndustry?.includes(id) || false;
      } else {
        return false;
      }
    };

    if (filteredQuestionnaires && product) {
      setFilteredQuestionnaires(filteredQuestionnaires.map((data) => {
        return {
          ...data,
          resultSets: data.resultSets.map((resultSet) => {
            return {
              ...resultSet,
              checked: checkIfIsChecked(data.type, resultSet.id),
            };
          }),
        };
      }));
    }
  };

  const filterLeadQuestionnaires = () => {
    if (leadQuestionnaires) {
      setFilteredLeadQuestionnaires(leadQuestionnaires.map((data) => {
        return {
          ...data,
          questionnaires: data.questionnaires.map((questionnaire) => {
            return {
              ...questionnaire,
              checked: (product?.leadQuestionnaires || []).includes(questionnaire.id),
            };
          }),
        };
      }));
    }
  };

  const fetchQuestionnaires = () => {
    return axios.get('/api/questionnaire/main');
  };

  const fetchLeadQuestionnaires = () => {
    return axios.get('/api/questionnaire/lead');
  };

  const fetchProductCategories = () => {
    return axios.get('/api/product/category/all');
  };

  const fetchAttachment = (id: string) => {
    return axios.get(`/api/attachment/${id}`);
  };

  useEffect(() => {
    fetchProductCategories().then((response) => {
      setCategoriesList(response.data.map((category: ProductCategory) => {
        return { id: category.id, name: category.name };
      }));
    }).catch((e) => {
      console.log(e);
    });

  }, []);

  useEffect(() => {
    // update tempCustomer on props change
    setTempProduct(product);

    if (product && product.attachments) {
      const requests = product.attachments.map((attachmentId: string) => {
        return fetchAttachment(attachmentId);
      });

      Promise.all(requests).then((responses) => {
        const data = [...responses.map((response) => response.data)];
        setProductAttachments(data);
      }).catch((e) => {
        console.log(e);
      });
    }

    const getPossibleResultSets = (questionnaire: QuestionnaireWithResultSets): string[] => {
      const resultSets = questionnaire.resultSets || [];
      const additionalResultSets = questionnaire.additionalResultSets || [];
      if (additionalResultSets.length > 0) {
        return additionalResultSets.flatMap((item) => {
          return item.extends.map((id) => `${id}${item.id}`);
        }).filter((item, index, array) => array.indexOf(item) === index);
      }
      return resultSets.map(({ id }) => id).filter((item, index, array) => array.indexOf(item) === index);
    }

    fetchQuestionnaires().then((response) => {
      // console.log('GET /questionnaire/main', response);
      setQuestionnaires(response.data);
      const temp = response.data.map((questionnaire: QuestionnaireWithResultSets) => {
        return {
          id: questionnaire.id,
          title: questionnaire.description,
          type: questionnaire.questionnaire.type,
          resultSets: getPossibleResultSets(questionnaire).map((resultSetId) => {
            return {
              id: resultSetId,
              checked: false,
            };
          }),
        };
      });
      setFilteredQuestionnaires(temp);
    }).catch((error) => {
      console.error('GET /questionnaire/main', error);
    });

    fetchLeadQuestionnaires().then((response) => {
      // console.log('GET /questionnaire/lead', response);
      const data = [{
        label: 'Lead-Fragenbögen',
        questionnaires: [...response.data].map((questionnaire) => {
          return {
            id: questionnaire.questionnaire.type,
            label: questionnaire.description,
            checked: false,
          };
        }),
      }];
      setLeadQuestionnaires(data);
    }).catch((e) => {
      console.error('GET /questionnaire/lead', e);
    });

  }, [product]);

  useEffect(() => {
    refreshFilteredQuestionnaires();
  }, [questionnaires]);

  useEffect(() => {
    filterLeadQuestionnaires();
  }, [leadQuestionnaires]);

  return (
    <>
    <Headline>Produkt {product === null ? 'Hinzufügen' : 'Bearbeiten'}</Headline>
    <Wrapper>
      <Form>
        <Fieldset>
          <Headline>Produkt Stammdaten</Headline>
          <DefaultInput
            id={'name'}
            onChange={(event) => {
              setTempProduct({ ...tempProduct, name: event.target.value });
            }}
            type={'text'}
            label={'Produktname'}
            placeholder={'Produktname'}
            value={tempProduct?.name}
            required={true}
            hasError={validationState?.name} />
          <DefaultInput
            id={'displayName'}
            onChange={(event) => {
              setTempProduct({ ...tempProduct, displayName: event.target.value });
            }}
            type={'text'}
            label={'Anzeigename'}
            placeholder={'Anzeigename'}
            value={tempProduct?.displayName}
            required={false} />
          <DropDown
            id={'product-type'}
            onChange={(data) => {
              console.log("TEST", data.target.value)
              setTempProduct({ ...tempProduct, type: data.target.value });
            }}
            label={'Produktart'}
            value={tempProduct?.type}
            options={PRODUCT_ITEM_TYPES.map((type) => ({
              key: type, value: type,
            }))}
            required={true}
            hasError={validationState?.type} />
          {
            categoriesList && <SuggestionDropDown
              id={'product-category'}
              label={'Produktkategorie'}
              onChange={(suggestion) => {
                setTempProduct({ ...tempProduct, productCategoryId: suggestion.id });
              }}
              value={(() => {
                const currentCategory = categoriesList?.find((category) => category.id === tempProduct?.productCategoryId) || null;
                if (currentCategory) {
                  return {
                    id: currentCategory.id,
                    text: currentCategory.name,
                  };
                }
                return null;
              })()}
              suggestions={categoriesList.map((category) => {
                return {
                  id: category.id,
                  text: category.name,
                };
              })}
              hasError={validationState.productCategoryId} />
          }
          <Toggle
            id={'eco'}
            label={'Öko-Produkt?'}
            value={tempProduct?.eco}
            onChange={(checked) => {
              setTempProduct({ ...tempProduct, eco: checked });
            }} />
          <TextBox
            id={'description'}
            onChange={(event) => {
              setTempProduct({ ...tempProduct, description: event.target.value });
            }}
            label={'Beschreibung'}
            placeholder={''}
            value={tempProduct?.description}
            required={true}
            hasError={validationState.description} />
          <SelectBox
            multi={true}
            id={'target-groups'}
            onChange={(data: string[]) => {
              setTempProduct({ ...tempProduct, targetGroups: data });
            }}
            label={'Zielgruppen'}
            value={tempProduct?.targetGroups || []}
            options={[
              { key: CustomerTypes.BUSINESS, value: 'Gewerbekunden', icon: 'customers-commercial' },
              { key: CustomerTypes.HOUSING_INDUSTRY, value: 'Wohnungswirtschaft', icon: 'customers-housing' },
              { key: CustomerTypes.MAJOR_CUSTOMERS, value: 'Großkunden', icon: 'customers-major' },
            ]}
            required={true}
            hasError={validationState.targetGroups} />
        </Fieldset>
        <Fieldset>
          <Headline>Produktdetails</Headline>
          <FileUpload
            allowedFileTypes={['application/json']}
            label={''}
            multiFileUpload={false}
            id={'meta-product'}
            hasError={validationState.metaProduct}
            onChange={(files) => {
              const baseFile = files[0].dataUrl.replace('data:application/json;base64,', '') || '';

              const b64DecodeUtf8 = (str: string) => {
                return decodeURIComponent(atob(str).split('').map((c) => {
                  return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));
              };

              const json = JSON.parse(b64DecodeUtf8(baseFile)) || null;
              if (json && isObjectOfTypeMetaProduct(json)) {
                setTempProduct({ ...tempProduct, metaProduct: json });
              } else {
                showNotificationBanner({
                  error: true,
                  renderContent: () => <>Das von ihnen hinzugefügte Json-Dokument entspricht nicht den Richtlinien.</>,
                });
              }
            }} />
          <br />
          {
            (tempProduct && tempProduct.metaProduct) && <Button onClick={() => {
              const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(tempProduct.metaProduct));
              const dlAnchorElem = document.createElement('a');
              dlAnchorElem.setAttribute('href', dataStr);
              dlAnchorElem.setAttribute('download', `${tempProduct.name}-meta-product.json`);
              dlAnchorElem.click();
            }}>Download Produktdetails</Button>
          }
        </Fieldset>
        <Fieldset>
          <Headline>Dokumente</Headline>
          <FileUpload
            allowedFileTypes={['application/pdf', 'image/png', 'image/jpg']}
            label={''}
            multiFileUpload={true}
            id={'file-upload'}
            onChange={(files) => {
              setTempAttachments(files);
            }} />
        </Fieldset>
      </Form>
    </Wrapper>
    {
      (productAttachments && productAttachments.length > 0) ? <>
          <Headline divideContent={true}>Dokumente zum Produkt</Headline>
          <GenericList
            columns={[{ name: 'fileName', displayName: 'Dokumente', sortable: false }]}
            collapsible={true}
            items={productAttachments}
            additionalRenderFn={(file: FileUploadFile) => {
              return <>
                <ToggleInnerWrapper>
                  <DownloadLink onClick={() => {
                    const a = document.createElement("a");
                    a.href = file.dataUrl;
                    a.download = file.fileName;
                    a.click();
                  }}>Anzeigen</DownloadLink>
                  <CopyIdButton id={file.id} />
                  <DeleteAttachmentIconWrapper onClick={() => {
                    showConfirmationPopup({
                      open: true,
                      actionButtonLabel: 'Löschen',
                      closeFn() {
                        showConfirmationPopup(null);
                      },
                      actionFn() {
                        axios.post('/api/product/attachment', {
                          productId: product.id,
                          attachmentId: file.id,
                        }).then((response) => {
                          showNotificationBanner({
                            renderContent: () => {
                              return <>Dokument wurde gelöscht.</>;
                            },
                          });
                          showConfirmationPopup(null);
                          history.go(0);
                        }).catch((error) => {
                          showNotificationBanner({
                            error: true, renderContent: () => {
                              return <>Dokument konnte nicht gelöscht werden.</>;
                            },
                          });
                          showConfirmationPopup(null);
                        });
                      },
                      renderer() {
                        return <>Wollen Sie das Dokument wirklich löschen?</>;
                      },
                    });
                  }}>
                    <Icon icon={'trash'} size={24} />
                  </DeleteAttachmentIconWrapper>
                </ToggleInnerWrapper>
              </>;
            }} />
        </> :
        <NoDocumentsTeaser>Anhänge können erst angezeigt werden, wenn das Produkt erstellt wurde!</NoDocumentsTeaser>
    }
    {filteredQuestionnaires ? <>
      <Headline
        divideContent={true}>
        Lösungsansichten
      </Headline>
      <GenericList
        collapsible={true}
        columns={[{ name: 'title', displayName: 'Name', sortable: false }]}
        items={filteredQuestionnaires}
        additionalRenderFn={(questionnaire) => {
          return <AdditionalProductResultSetsListContent
            useSelectAll={true}
            item={questionnaire}
            onChange={(item) => {
              const result = [...filteredQuestionnaires].filter((questionnaire) => questionnaire.id !== item.id);
              result.push(item);
              setFilteredQuestionnaires(result);
            }} />;
        }} />
    </> : <NoDocumentsTeaser>Keine Fragebögen bzw. zugehörige Lösungsansichten gefunden!</NoDocumentsTeaser>}
    {filteredLeadQuestionnaires ? <>
      <Headline
        divideContent={true}>
        Lead-Fragebögen
      </Headline>
      <GenericList
        collapsible={true}
        columns={[{ name: 'label', displayName: 'Name', sortable: false }]}
        items={filteredLeadQuestionnaires}
        additionalRenderFn={(questionnaire) => {
          return <AdditionalProductLeadQuestionnaireListContent
            item={questionnaire}
            onChange={(item: LeadQuestionnaireQuestionnaireStateProp) => {
              const result = [...filteredLeadQuestionnaires].map((data) => {
                return {
                  ...data,
                  questionnaires: data.questionnaires.map((questionnaire) => {
                    if (questionnaire.id !== item.id) {
                      return questionnaire;
                    } else {
                      return item;
                    }
                  }),
                };
              });
              setFilteredLeadQuestionnaires(result);
            }} />;
        }} />
    </> : <NoDocumentsTeaser>Keine Lead-Fragebögen gefunden!</NoDocumentsTeaser>}
    <ButtonWrapper>
      <LinkButton onChangeHandler={(event: React.FormEvent<HTMLButtonElement>) => {
        onCancel && onCancel();
      }}>Abbrechen</LinkButton>
      <LinkButton onChangeHandler={(event: React.FormEvent<HTMLButtonElement>) => {

        const validations = validate();
        const inValid = Object.keys(validations).map((key) => {
          return validations[key];
        }).filter(Boolean).length > 0;

        if (!inValid) {

          let resultSets: any = null;
          let leadQuestionnaires: string[] | null = null;

          if (filteredQuestionnaires) {
            const business = filteredQuestionnaires.find((questionnaire) => questionnaire.type === 'BUSINESS') || null;
            const businessResultSets = business ? business.resultSets.map((resultSet) => {
              if (resultSet.checked) {
                return resultSet.id;
              } else {
                return null;
              }
            }) : [];

            const majorCustomers = filteredQuestionnaires.find((questionnaire) => questionnaire.type === 'MAJOR_CUSTOMERS') || null;
            const majorCustomersResultSets = majorCustomers ? majorCustomers.resultSets.map((resultSet) => {
              if (resultSet.checked) {
                return resultSet.id;
              } else {
                return null;
              }
            }) : [];

            const housingIndustry = filteredQuestionnaires.find((questionnaire) => questionnaire.type === 'HOUSING_INDUSTRY') || null;
            const housingIndustryResultSets = housingIndustry ? housingIndustry.resultSets.map((resultSet) => {
              if (resultSet.checked) {
                return resultSet.id;
              } else {
                return null;
              }
            }) : [];

            resultSets = {
              business: [...businessResultSets.filter((resultSet) => resultSet !== null)],
              majorCustomers: [...majorCustomersResultSets.filter((resultSet) => resultSet !== null)],
              housingIndustry: [...housingIndustryResultSets.filter((resultSet) => resultSet !== null)],
            };
          }

          if (filteredLeadQuestionnaires) {
            leadQuestionnaires = [];
            [...filteredLeadQuestionnaires[0].questionnaires].filter((questionnaire) => {
              if (questionnaire.checked) {
                if (questionnaire.id === 'LEAD_DEFAULT') {
                  leadQuestionnaires = [questionnaire.id, ...leadQuestionnaires];
                } else {
                  leadQuestionnaires.push(questionnaire.id);
                }
              }
            });
          }

          setProduct({
            product: {
              ...tempProduct,
              resultSets: resultSets || tempProduct.resultSets,
              leadQuestionnaires: leadQuestionnaires || tempProduct.leadQuestionnaires,
            },
            attachments: tempAttachments,
          });
        } else {
          showNotificationBanner({
              error: true,
              renderContent: () => {
                return <>Bitte füllen Sie alle Rot markierten Felder aus.</>;
              },
            },
          );
        }
      }}>Speichern</LinkButton>
    </ButtonWrapper>
  </>
  );
};
